import {
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { ChevronLeft, ChevronRight, GetApp } from '@material-ui/icons'
import { Confirm, FlexHorizontal, LoadMask, Spacer, useToasts } from '@vestaboard/installables'
import {
  MPL_SUBSCRIPTIONS_DEVICE_QUERY,
  useMarketplaceListingSubscriptions
} from '../hooks/useMarketplaceListingSubscriptions'
import React, { useState } from 'react'
import { useDeleteInstallation, useDeleteSubscription, useEditSubscriptionMuted } from '../../../api'

import { Alert } from '@material-ui/lab'
import { CsvExport } from '../../../ui/CsvExport'
import { FormattedDateTime } from '../../formatted-date-time'
import { SubscriptionSimulateControls } from '../../shared/SubscriptionSimulateControls'
import { TruncatedId } from '../../truncated-id'
import { csvDownload } from '../../../utils'
import { csvFormat } from 'd3-dsv'
import moment from 'moment'
import { useGetListingSubscriberMembers } from '../hooks/useGetListingSubscriberMembers'

interface IListSubscriptions {
  id: string
}

export const ListSubscriptions = (props: IListSubscriptions) => {
  const { fetchSubscriberMembers } = useGetListingSubscriberMembers(props.id)
  const [page, setPage] = useState(0)
  const { loading, data, error, refetch } = useMarketplaceListingSubscriptions(props.id, {
    perPage: 25,
    page
  })
  const [simulateSubscriptionConfig, setSimulateSubscriptionConfig] = useState('')
  const [deleteInstallation] = useDeleteInstallation()
  const [deleteSubscription] = useDeleteSubscription()
  const [editSubscriptionMuted] = useEditSubscriptionMuted()
  const [isDeletingSubscription, setIsDeletingSubscription] = useState<string | null>(null)
  const [isDeletingInstallation, setIsDeletingInstallation] = useState<string | null>(null)
  const [fetchingCsv, setFetchingCsv] = useState(false)
  const { addToast } = useToasts()

  return loading ? (
    <CircularProgress />
  ) : error ? (
    <Alert severity='error'>Unable to load subscriptions for this installable.</Alert>
  ) : (
    <>
      {fetchingCsv ? <LoadMask /> : null}
      <Confirm
        title='Delete Installation?'
        open={!!isDeletingInstallation}
        handleClose={() => {
          setIsDeletingInstallation(null)
        }}
        handleAccept={async () => {
          if (isDeletingInstallation) {
            try {
              await deleteInstallation({
                variables: {
                  installationId: isDeletingInstallation
                }
              })
            } catch (e) {}
            setIsDeletingInstallation(null)
            await refetch()
            addToast('Installation deleted', {
              appearance: 'success'
            })
          }
        }}
      />
      <Confirm
        title='Delete Subscription?'
        open={!!isDeletingSubscription}
        handleClose={() => {
          setIsDeletingSubscription(null)
        }}
        handleAccept={async () => {
          if (isDeletingSubscription) {
            try {
              await deleteSubscription({
                variables: {
                  subscriptionId: isDeletingSubscription
                }
              })
            } catch (e) {}
            setIsDeletingSubscription(null)
            await refetch()
            addToast('Subscription deleted', {
              appearance: 'success'
            })
          }
        }}
      />
      <Dialog
        maxWidth='lg'
        style={{ width: 1000, height: 800 }}
        open={!!simulateSubscriptionConfig}
        onClose={() => setSimulateSubscriptionConfig('')}>
        <iframe style={{ width: 1000, height: 800 }} src={simulateSubscriptionConfig} title='simulator config' />
      </Dialog>
      <Spacer size='medium' />
      <FlexHorizontal>
        <CsvExport
          label='Export Subscriptions'
          query={MPL_SUBSCRIPTIONS_DEVICE_QUERY}
          variables={{
            id: props.id
          }}
          formatData={function (data: any): any[] {
            const results = data?.data?.marketplaceListing?.adminInstallations?.map((installation: any) => {
              const subscription = installation?.subscriptions?.[0]
              const subscriptionId = subscription?.id || ''
              const boardId = installation?.subscriptions?.[0]?.subscriptionSet?.boards?.[0]?.id || ''
              const boardTitle = installation?.subscriptions?.[0]?.subscriptionSet?.boards?.[0]?.title || ''
              return {
                installationId: installation.id,
                subscriptionId,
                boardId,
                boardTitle,
                created: installation.created,
                createdFormatted: moment(+installation.created).format('YYYY-MM-DD hh:mm a'),
                deviceId: subscription?.subscriptionSet?.boards?.[0]?.devices?.[0]?.id,
                pairingCode: subscription?.subscriptionSet?.boards?.[0]?.devices?.[0]?.pairingCode,
                firmwareVersion: subscription?.subscriptionSet?.boards?.[0].devices?.[0]?.firmwareVersion,
                latestHeartbeat: subscription?.subscriptionSet?.boards?.[0].devices?.[0]?.latestHeartbeat?.created,
                latestHeartbeatFormatted: moment(
                  subscription?.subscriptionSet?.boards?.[0].devices?.[0]?.latestHeartbeat?.created
                ).format('YYYY-MM-DD hh:mm a'),
                enabled:
                  subscription?.runOfShow?.enabled == null
                    ? ''
                    : subscription.runOfShow.enabled
                    ? 'Enabled'
                    : 'Disabled',
                muted: subscription?.muted ? 'True' : 'False',
                cadence: subscription?.runOfShow?.description || '',
                userTitle: subscription?.title || ''
              }
            })
            return results || []
          }}
        />
        <div style={{ width: 7 }} />
        <Button
          variant='outlined'
          endIcon={<GetApp />}
          onClick={async () => {
            setFetchingCsv(true)
            const personIds = await fetchSubscriberMembers(true)
            const csv = csvFormat(
              personIds.map(id => ({ personId: id })),
              ['personId']
            )
            csvDownload(csv)
            setFetchingCsv(false)
          }}>
          Export All Person IDs
        </Button>
        <div style={{ width: 7 }} />
        <Button
          variant='outlined'
          endIcon={<GetApp />}
          onClick={async () => {
            setFetchingCsv(true)
            const personIds = await fetchSubscriberMembers(false)
            const csv = csvFormat(
              personIds.map(id => ({ personId: id })),
              ['personId']
            )
            csvDownload(csv)
            setFetchingCsv(false)
          }}>
          Export Active Person IDs
        </Button>
      </FlexHorizontal>
      <Spacer />
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Installation ID</TableCell>
                <TableCell>Subscription ID</TableCell>
                <TableCell>Board ID</TableCell>
                <TableCell>Board Title</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Simulate</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>Pairing Code</TableCell>
                <TableCell>Firmware Version</TableCell>
                <TableCell>Latest Heartbeat</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Muted</TableCell>
                <TableCell>Cadence</TableCell>
                <TableCell>User Title</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.marketplaceListing?.adminInstallations?.map(installation => {
                const subscription = installation.subscriptions?.[0]
                const subscriptionId = subscription?.id || ''
                const boardId = installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].id || ''
                const boardTitle = installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].title || ''

                return (
                  <TableRow key={installation.id}>
                    <TableCell>
                      <TruncatedId value={installation.id} path={`/installation/${installation.id}`} />
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={subscriptionId} path={`/subscription/${subscriptionId}`} />
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={boardId} path={`/boards/${boardId}`} />
                    </TableCell>
                    <TableCell>{boardTitle}</TableCell>
                    <TableCell>
                      <FormattedDateTime value={+installation.created} />
                    </TableCell>
                    <TableCell>
                      <SubscriptionSimulateControls
                        subscriptionId={subscription?.id ?? ''}
                        configurationUrl={subscription?.configurationUrl}
                        onSimulate={setSimulateSubscriptionConfig}
                      />
                    </TableCell>
                    <TableCell>
                      <TruncatedId
                        value={installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].devices?.[0]?.id}
                      />
                    </TableCell>
                    <TableCell>
                      {installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].devices?.[0]?.pairingCode}
                    </TableCell>
                    <TableCell>
                      {installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].devices?.[0]?.firmwareVersion}
                    </TableCell>
                    <TableCell
                      style={{
                        whiteSpace: 'nowrap'
                      }}>
                      <FormattedDateTime
                        value={
                          installation.subscriptions?.[0]?.subscriptionSet.boards?.[0].devices?.[0]?.latestHeartbeat
                            ?.created
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {subscription?.runOfShow?.enabled == null
                        ? '—'
                        : subscription.runOfShow.enabled
                        ? 'Enabled'
                        : 'Disabled'}
                    </TableCell>
                    <TableCell>{subscription?.muted ? 'True' : 'False'}</TableCell>
                    <TableCell>{subscription?.runOfShow?.description || '—'}</TableCell>
                    <TableCell>{subscription?.title || '—'}</TableCell>
                    <TableCell>
                      <Button
                        variant='outlined'
                        color='secondary'
                        onClick={() => {
                          if (subscription) {
                            setIsDeletingSubscription(subscription.id)
                          } else {
                            setIsDeletingInstallation(installation.id)
                          }
                        }}>
                        Delete
                      </Button>
                      <Button
                        onClick={() => {
                          editSubscriptionMuted({
                            variables: {
                              subscriptionId: subscriptionId,
                              muted: !subscription?.muted
                            }
                          })
                        }}>
                        {subscription?.muted ? 'Unmute' : 'Mute'}
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {!data?.marketplaceListing?.adminInstallations?.length ? (
          <Alert severity='warning'>There are no subscriptions to see here...</Alert>
        ) : null}
      </Card>
      <Spacer size='large' />
      <Box display='flex'>
        <ButtonBase
          disabled={!page}
          onClick={() => {
            setPage(page => page - 1)
          }}>
          <ChevronLeft />
        </ButtonBase>
        <Typography>Page {page + 1}</Typography>
        <ButtonBase
          disabled={!data?.marketplaceListing?.adminInstallations?.length}
          onClick={() => {
            setPage(page => page + 1)
          }}>
          <ChevronRight />
        </ButtonBase>
      </Box>
    </>
  )
}
